import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import {
  SELECT_PROP_TYPES,
  SELECT_AUTOCOMPLETE_PROP_TYPES,
  SELECT_MENU_PROP_TYPES,
  SELECT_MULTIPLE_PROP_TYPES,
} from './constants';

function SelectAutocomplete(props) {
  const {
    disabled,
    display,
    error,
    events = {},
    label,
    name,
    helperText,
    size,
    value,
    onBlur,
    onChange,
    loading = false,
  } = props;
  let { options = [] } = props;
  const inputStyle = {
    borderRadius: '12px',
    height: '44px',
    border: '#CED8E3',
    ...display?.inputStyle,
  };

  if (typeof options === 'function') {
    options = options();
  }

  return (
    <Autocomplete
      disabled={disabled || loading}
      loading={loading}
      onChange={(event, value) => {
        const target = {
          name,
          value: value?.id,
        };

        onChange({
          target, 
        });

        if (events.onChange) {
          events.onChange(target);
        }
      }}
      onBlur={onBlur}
      options={options}
      getOptionLabel={({ name }) => name}
      groupBy={({ groupBy }) => groupBy}
      defaultValue={options.find((_) => String(_.id) === String(value))}
      renderInput={(params) => (
        <TextField
          {...params}
          color="secondary"
          name={name}
          fullWidth
          InputProps={{
            ...params.InputProps,
            style: {
              ...inputStyle,
            },
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          size={size}
          error={error}
          helperText={helperText}
          label={label}
          variant="outlined"
        />
      )}
    />
  );
}

function SelectMenu(props) {
  const {
    disabled,
    display,
    emptyElement,
    error,
    events = {},
    group,
    helperText,
    label,
    name,
    onBlur,
    onChange,
    size,
    value,
    loading = false,
  } = props;
  let { options = [] } = props;
  const inputStyle = {
    borderRadius: '12px',
    height: '44px',
    border: '#CED8E3',
    ...display?.inputStyle,
  };
  const menuProps = {
    PaperProps: {
      style: {
        borderRadius: 12,
        marginTop: 8,
        border: '1px solid #E2E8F0',
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.15)',
      },
    },
    MenuListProps: {
      style: {
        paddingTop: 6,
        paddingBottom: 6,
      },
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
  };

  if (typeof options === 'function') {
    options = options();
  }

  const groups = group
    ? [...new Set(options.map(({ groupBy }) => groupBy))]
    : [];

  return (
    <Box position="relative">
      <TextField
        autoComplete
        color="secondary"
        disabled={disabled || loading}
        error={error}
        fullWidth
        helperText={helperText}
        InputProps={{
          style: {
            ...inputStyle,
          },
        }}
        InputLabelProps={{
          shrink: true,
        }}
        label={label}
        name={name}
        select
        SelectProps={{
          displayEmpty: !!emptyElement,
          MenuProps: menuProps,
        }}
        size={size}
        variant="outlined"
        value={value}
        onBlur={onBlur}
        onChange={(e) => {
          onChange(e);
          if (events.onChange) {
            events.onChange(e?.target);
          }
        }}
      >
        {emptyElement && (
          <MenuItem value={emptyElement} style={{
            color: '#94A3B8',
            fontStyle: 'normal',
          }}>
            {emptyElement}
          </MenuItem>
        )}
        {group
          ? groups.reduce((acc, item) => {
            const opt = options.filter((_) => _.groupBy === item);
            if (!opt.length) {
              return acc;
            }

            acc.push(
              <ListSubheader
                key={`group_${item}`}
                disableSticky
                style={{
                  lineHeight: '32px',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: '#6B7A90',
                  backgroundColor: '#F8FAFC',
                }}
              >
                {item}
              </ListSubheader>,
            );

            opt.forEach(({ id, name }, key) => {
              acc.push(
                <MenuItem value={id} key={`${item}_${id}_${key}`}>
                  {name}
                </MenuItem>,
              );
            });

            return acc;
          }, [])
          : options.map(({ id, name }, key) => (
            <MenuItem value={id} key={key}>
              {name}
            </MenuItem>
          ))}
      </TextField>
      {loading && (
        <CircularProgress
          size={20}
          style={{
            position: 'absolute',
            top: '50%',
            right: '10px',
            marginTop: '-10px',
          }}
        />
      )}
    </Box>
  );
}

function SelectMultiple(props) {
  const {
    disabled,
    error,
    events = {},
    helperText,
    label,
    name,
    onChange,
    size,
    loading = false,
  } = props;
  let { options = [], value = [] } = props;
  const menuProps = {
    PaperProps: {
      style: {
        borderRadius: 12,
        marginTop: 8,
        border: '1px solid #E2E8F0',
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.15)',
      },
    },
    MenuListProps: {
      style: {
        paddingTop: 6,
        paddingBottom: 6,
      },
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
  };

  if (!Array.isArray(value)) {
    value = [];
  }

  if (typeof options === 'function') {
    options = options();
  }

  return (
    <FormControl fullWidth size={size} variant="outlined" color="secondary" style={{
      position: 'relative', 
    }}>
      <InputLabel children={label} />
      <Select
        disabled={disabled || loading}
        error={error}
        input={<OutlinedInput label={label} color="secondary" />}
        MenuProps={menuProps}
        multiple
        name={name}
        onChange={(e) => {
          onChange(e);
          if (events.onChange) {
            events.onChange(e?.target);
          }
        }}
        renderValue={(selected) =>
          `${selected.length} seleccionado${selected.length !== 1 ? 's' : ''}`
        }
        value={value}
        variant="outlined"
      >
        {options.map((option, index) => (
          <MenuItem key={index} value={option.id}>
            {!disabled && <Checkbox checked={value.includes(option.id)} />}
            <ListItemText primary={option.name} />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText children={helperText} error />
      {loading && (
        <CircularProgress
          size={20}
          style={{
            position: 'absolute',
            top: '50%',
            right: '10px',
            marginTop: '-10px',
          }}
        />
      )}
    </FormControl>
  );
}

function SelectControl(props) {
  const {
    disabled,
    display = {},
    label,
    name,
    settings: {
      autocomplete = false,
      emptyElement,
      group = false,
      multiple = false,
      options = [],
    },
    events = {},
    size,
    value,
    error,
    helperText,
    onBlur,
    onChange,
    loading = false,
  } = props;
  const labelStyle = {
    textTransform: 'none',
    letterSpacing: '0.08em',
    fontSize: '11px',
    fontWeight: 700,
    color: '#6B7A90',
    ...display?.labelStyle,
  };
  if (disabled) {
    labelStyle.color = '#0000004e';
  }

  const Control = multiple
    ? SelectMultiple
    : autocomplete
      ? SelectAutocomplete
      : SelectMenu;

  return (
    <Box sx={{
      mb: 2, 
    }}>
      <Typography style={{
        ...labelStyle,
      }} variant="h6" gutterBottom>
        {label}
      </Typography>
      <Control
        disabled={disabled}
        display={display}
        emptyElement={emptyElement}
        error={error}
        events={events}
        group={group}
        helperText={helperText}
        multiple={multiple}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        options={options}
        size={size}
        value={value}
        loading={loading}
      />
    </Box>
  );
}

SelectAutocomplete.propTypes = SELECT_AUTOCOMPLETE_PROP_TYPES;
SelectMenu.propTypes = SELECT_MENU_PROP_TYPES;
SelectMultiple.propTypes = SELECT_MULTIPLE_PROP_TYPES;
SelectControl.propTypes = SELECT_PROP_TYPES;

SelectControl.defaultProps = {
  disabled: false,
  settings: {
    autocomplete: false,
    group: false,
    options: [],
  },
  size: 'small',
  onChange: () => {},
  onBlur: () => {},
};

export default SelectControl;
